import React, { useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Gauge } from 'lucide-react';

/**
 * SeoScore — a live, deterministic SEO checklist + 0-100 score.
 *
 * Pure function of the SEO record (+ optional page content for word
 * count / image-alt checks). No backend call: re-computes on every
 * keystroke in the editor so the writer sees the number move.
 *
 * Scoring is weighted: the title + meta-description are the heavy
 * hitters Google actually shows; OG/Twitter/canonical/keywords round
 * it out. Each check is pass / warn / fail and contributes its weight
 * (pass = full, warn = half, fail = 0).
 */
function buildChecks(seo = {}, page = null) {
  const title = (seo.title || '').trim();
  const desc = (seo.description || '').trim();
  const kw = Array.isArray(seo.keywords) ? seo.keywords : (seo.keywords ? String(seo.keywords).split(',') : []);
  const og = (seo.og_title || '').trim();
  const ogImg = (seo.og_image || '').trim();
  const tw = (seo.twitter_title || '').trim();
  const canonical = (seo.canonical || '').trim();
  const schema = seo.schema_jsonld;

  // Pull plain text from the page sections for a rough word count + alt check.
  let bodyText = '';
  let imageFields = 0;
  let imagesWithAlt = 0; // we can't know alt per page-image yet; treat as informational
  if (page?.sections) {
    for (const s of page.sections) {
      for (const f of (s.fields || [])) {
        if (typeof f.value === 'string') bodyText += ' ' + f.value;
        if (f.type === 'image' && f.value) imageFields += 1;
      }
    }
  }
  const words = bodyText.trim() ? bodyText.trim().split(/\s+/).length : 0;

  const checks = [
    {
      key: 'title', weight: 22, label: 'Title tag',
      status: !title ? 'fail' : (title.length < 30 || title.length > 60) ? 'warn' : 'pass',
      hint: !title ? 'Add a title — this is what shows in Google results.'
        : title.length > 60 ? `Title is ${title.length} chars; Google truncates ~60. Trim it.`
        : title.length < 30 ? `Title is only ${title.length} chars; aim for 50–60 to use the space.`
        : `Good length (${title.length} chars).`,
    },
    {
      key: 'desc', weight: 22, label: 'Meta description',
      status: !desc ? 'fail' : (desc.length < 70 || desc.length > 160) ? 'warn' : 'pass',
      hint: !desc ? 'Add a meta description — your sales pitch in search results.'
        : desc.length > 160 ? `Description is ${desc.length} chars; Google cuts ~160. Trim it.`
        : desc.length < 70 ? `Description is ${desc.length} chars; 140–160 reads fuller.`
        : `Good length (${desc.length} chars).`,
    },
    {
      key: 'keywords', weight: 8, label: 'Focus keywords',
      status: kw.filter(Boolean).length === 0 ? 'warn' : 'pass',
      hint: kw.filter(Boolean).length === 0 ? 'Add a few target keywords to guide your copy.' : `${kw.filter(Boolean).length} keyword(s) set.`,
    },
    {
      key: 'canonical', weight: 8, label: 'Canonical URL',
      status: !canonical ? 'warn' : 'pass',
      hint: !canonical ? 'Set a canonical URL to avoid duplicate-content issues.' : 'Canonical set.',
    },
    {
      key: 'og', weight: 12, label: 'OpenGraph (social share)',
      status: (!og && !ogImg) ? 'fail' : (!og || !ogImg) ? 'warn' : 'pass',
      hint: (!og && !ogImg) ? 'Add an og:title + og:image so links look good on LinkedIn / Slack.'
        : !ogImg ? 'Add an og:image (1200×630) — links without an image get far fewer clicks.'
        : !og ? 'Add an og:title.' : 'OpenGraph title + image set.',
    },
    {
      key: 'twitter', weight: 6, label: 'Twitter card',
      status: !tw ? 'warn' : 'pass',
      hint: !tw ? 'Add a Twitter title for nicer X/Twitter previews.' : 'Twitter card set.',
    },
    {
      key: 'schema', weight: 8, label: 'Structured data (schema.org)',
      status: !schema ? 'warn' : 'pass',
      hint: !schema ? 'Add JSON-LD structured data for rich results.' : 'Structured data present.',
    },
    {
      key: 'words', weight: 8, label: 'Content depth',
      status: page == null ? 'na' : words < 100 ? 'warn' : 'pass',
      hint: page == null ? 'Body word count unavailable for this page type.'
        : words < 100 ? `Only ~${words} words of copy; thin pages rank poorly. Aim for 300+.`
        : `~${words} words of copy.`,
    },
    {
      key: 'images', weight: 6, label: 'Imagery',
      status: page == null ? 'na' : imageFields === 0 ? 'warn' : 'pass',
      hint: page == null ? 'N/A' : imageFields === 0 ? 'No images on the page — visuals lift engagement + dwell time.' : `${imageFields} image field(s) on the page.`,
    },
  ];

  // Score: sum of (weight × statusFactor) / sum of applicable weights.
  const factor = { pass: 1, warn: 0.5, fail: 0, na: null };
  let got = 0, max = 0;
  for (const c of checks) {
    const f = factor[c.status];
    if (f === null) continue;       // skip N/A from the denominator
    max += c.weight;
    got += c.weight * f;
  }
  const score = max ? Math.round((got / max) * 100) : 0;
  return { checks, score };
}

const STATUS_ICON = {
  pass: { Icon: CheckCircle2, cls: 'text-emerald-500' },
  warn: { Icon: AlertTriangle, cls: 'text-amber-500' },
  fail: { Icon: XCircle, cls: 'text-rose-500' },
  na:   { Icon: AlertTriangle, cls: 'text-slate-400' },
};

function scoreTone(score) {
  if (score >= 80) return { ring: 'text-emerald-500', label: 'Strong', bg: 'from-emerald-500 to-teal-500' };
  if (score >= 55) return { ring: 'text-amber-500', label: 'Needs work', bg: 'from-amber-500 to-orange-500' };
  return { ring: 'text-rose-500', label: 'Weak', bg: 'from-rose-500 to-pink-500' };
}

export default function SeoScore({ seo, page = null }) {
  const { checks, score } = useMemo(() => buildChecks(seo, page), [seo, page]);
  const tone = scoreTone(score);
  const circ = 2 * Math.PI * 26;
  const offset = circ * (1 - score / 100);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-4 mb-4">
        {/* Score ring */}
        <div className="relative w-16 h-16 flex-shrink-0">
          <svg viewBox="0 0 60 60" className="w-16 h-16 -rotate-90">
            <circle cx="30" cy="30" r="26" fill="none" stroke="#e2e8f0" strokeWidth="6" />
            <circle
              cx="30" cy="30" r="26" fill="none" strokeWidth="6" strokeLinecap="round"
              className={tone.ring} stroke="currentColor"
              strokeDasharray={circ} strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 0.4s ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-extrabold text-slate-900">{score}</span>
          </div>
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Gauge size={14} /> SEO score: {tone.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">Live — updates as you edit. Aim for 80+.</p>
        </div>
      </div>

      <ul className="space-y-1.5">
        {checks.map((c) => {
          const s = STATUS_ICON[c.status] || STATUS_ICON.na;
          return (
            <li key={c.key} className="flex items-start gap-2 text-xs">
              <s.Icon size={14} className={`${s.cls} flex-shrink-0 mt-0.5`} />
              <div>
                <span className="font-semibold text-slate-800">{c.label}</span>
                <span className="text-slate-500"> — {c.hint}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
